import * as React from 'react';

import { Link, useLocation } from 'react-router-dom';

import { useIssuesSummary } from '@/api/products';
import { sellerApprovalTotal } from '@/api/stock/sellerWorkspace';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useAuth } from '@/hooks/useAuth';
import { useCameraAccess } from '@/hooks/useCameraAccess';
import { useInvoicesCounts } from '@/hooks/useInvoicesCounts';
import { useProductionPayrollAccess } from '@/hooks/useProductionPayrollAccess';
import { hasSellerWork, useSellerWorkspaceAccess } from '@/hooks/useSellerWorkspaceAccess';
import { cn } from '@/lib/cn';
import { canUseSellerProductionLocation } from '@/pages/stock/seller-workspace/sellerWorkspaceProductionAccess';
import { useSellerApprovalCounts } from '@/pages/stock/seller-workspace/useSellerApprovalCounts';
import { useSellerWorkspaceLocation } from '@/pages/stock/seller-workspace/useSellerWorkspaceLocation';

import { useDesktopSidebarHover } from './DesktopSidebarHover';
import {
  bottomItems,
  navGroups,
  navItems,
  productionPayrollNavGroup,
  sellerWorkspacePrimaryGroups,
  storeCamerasNavGroup,
  topLevelItems,
  type NavGroup,
  type NavItem,
} from './sidebarNavConfig';
import { UserDropdown } from './UserDropdown';

// ============================================================================
// Collapsible group state management
// ============================================================================

const COLLAPSED_GROUPS_STORAGE_KEY = 'sidebar-collapsed-groups';
const SELLER_BALANCES_NAV_HREF = '/stock/seller-workspace/balances';
const SELLER_PRODUCTION_NAV_HREF = '/stock/seller-workspace/productions';
const SELLER_EXPIRY_NAV_HREF = '/stock/seller-workspace/expiry';
const SELLER_COUNTS_NAV_HREF = '/stock/seller-workspace/counts';

function persistCollapsedGroups(collapsedGroups: Record<string, boolean>) {
  try {
    localStorage.setItem(COLLAPSED_GROUPS_STORAGE_KEY, JSON.stringify(collapsedGroups));
  } catch {
    // Ignore storage failures and keep the in-memory UI state usable.
  }
}

function isPathActive(currentPath: string, href: string, exact = false): boolean {
  if (exact) {
    return currentPath === href;
  }
  return currentPath === href || currentPath.startsWith(`${href}/`);
}

function isNavItemActive(currentPath: string, item: NavItem): boolean {
  const fullPath = `/webadmin/app${item.href}`;
  const activePaths = item.activePaths?.map((path) => `/webadmin/app${path}`) ?? [];
  return [fullPath, ...activePaths].some((path) => isPathActive(currentPath, path, item.exact));
}

function useSidebarGroupState(currentPath: string) {
  const [collapsedGroups, setCollapsedGroups] = React.useState<Record<string, boolean>>(() => {
    try {
      const stored = localStorage.getItem(COLLAPSED_GROUPS_STORAGE_KEY);
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  });

  // Find the group containing the active route
  const activeGroupTitle = React.useMemo(
    () => navGroups.find((g) => g.items.some((item) => isNavItemActive(currentPath, item)))?.title,
    [currentPath],
  );

  const isGroupCollapsed = React.useCallback(
    (title: string) => {
      // Never collapse the group with the active route
      if (activeGroupTitle === title) return false;
      return collapsedGroups[title] ?? false;
    },
    [activeGroupTitle, collapsedGroups],
  );

  const toggleGroup = React.useCallback(
    (title: string) => {
      // Don't toggle the active group — it's forced open
      if (activeGroupTitle === title) return;
      setCollapsedGroups((prev) => {
        const next = { ...prev, [title]: !prev[title] };
        persistCollapsedGroups(next);
        return next;
      });
    },
    [activeGroupTitle],
  );

  return { isGroupCollapsed, toggleGroup };
}

// ============================================================================
// Sidebar component
// ============================================================================

export interface SidebarProps {
  /** Whether sidebar is collapsed (mobile view) */
  collapsed?: boolean;
  /** Expands a collapsed sidebar only on hover/focus (desktop rail mode) */
  hoverExpand?: boolean;
  /** Callback when collapse state changes */
  onCollapsedChange?: (collapsed: boolean) => void;
  /** Additional class names */
  className?: string;
}

interface SidebarChromeProps {
  collapsed: boolean;
  className?: string | undefined;
  currentPath: string;
  filteredBottomItems: NavItem[];
  filteredNavGroups: NavGroup[];
  filteredTopLevelItems: NavItem[];
  overdueInvoicesCount: number;
  sellerApprovalsCount: number;
  onCollapsedChange?: ((collapsed: boolean) => void) | undefined;
  productsIssuesCount: number;
  isGroupCollapsed: (title: string) => boolean;
  toggleGroup: (title: string) => void;
  railMode?:
    | {
        expanded: boolean;
        onMouseEnter?: React.MouseEventHandler<HTMLElement>;
        onMouseLeave?: React.MouseEventHandler<HTMLElement>;
      }
    | undefined;
}

function SidebarChrome({
  collapsed,
  className,
  currentPath,
  filteredBottomItems,
  filteredNavGroups,
  filteredTopLevelItems,
  overdueInvoicesCount,
  sellerApprovalsCount,
  onCollapsedChange,
  productsIssuesCount,
  isGroupCollapsed,
  toggleGroup,
  railMode,
}: SidebarChromeProps) {
  const isHoverRailMode = railMode != null;
  const isCollapsedView = railMode ? !railMode.expanded : collapsed;
  const showManualToggle = !isHoverRailMode;
  const isTightHeader = isCollapsedView && showManualToggle;

  return (
    <aside
      className={cn(
        'relative h-full',
        // Hover rail expands in normal flow, so main content is smoothly compressed.
        railMode
          ? cn(
              'overflow-hidden transition-[width] duration-150 ease-out will-change-[width]',
              railMode.expanded ? 'w-52' : 'w-14',
            )
          : isCollapsedView
            ? 'w-14'
            : 'w-52',
        // Full height with safe area for fixed positioning
        className,
      )}
      data-collapsed={isCollapsedView}
      data-desktop-nav-zone="primary"
      onMouseEnter={railMode?.onMouseEnter}
      onMouseLeave={railMode?.onMouseLeave}
    >
      <div className="flex h-full w-full flex-col border-r border-border bg-background">
        {/* Logo / Brand area */}
        <div
          className={cn(
            'relative flex h-14 items-center border-b border-border',
            isCollapsedView ? 'pl-2 pr-1.5' : 'justify-between pl-3 pr-2',
          )}
        >
          <span
            className={cn(
              'truncate text-foreground',
              isCollapsedView
                ? 'text-xs font-semibold'
                : 'text-sm font-semibold tracking-[0.04em]',
              isTightHeader && 'max-w-[28px]',
            )}
          >
            ERP24
          </span>
          {showManualToggle ? (
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label={isCollapsedView ? 'Развернуть меню' : 'Свернуть меню'}
              onClick={() => onCollapsedChange?.(!collapsed)}
              className={cn(
                'flex-shrink-0',
                isTightHeader && 'absolute right-0.5 top-1/2 size-6 -translate-y-1/2 p-0',
              )}
            >
              <MenuToggleIcon collapsed={isCollapsedView} />
            </Button>
          ) : null}
        </div>

        {/* Navigation */}
        <ScrollArea
          className="flex-1 overflow-y-auto"
          orientation="vertical"
          scrollbarThickness={isCollapsedView ? 0 : 4}
          scrollbarInset={isCollapsedView ? 0 : 3}
          thumbMinSize={isCollapsedView ? 0 : 20}
        >
          <nav className="py-2 font-mono" aria-label="Основная навигация">
            <div className="space-y-3">
              {/* Top-level items (no group header) */}
              <div>
                <ul className="space-y-0.5 px-1.5" role="list">
                  {filteredTopLevelItems.map((item) => (
                    <NavItemLink
                      key={item.href}
                      item={item}
                      currentPath={currentPath}
                      collapsed={isCollapsedView}
                      productsIssuesCount={productsIssuesCount}
                      overdueInvoicesCount={overdueInvoicesCount}
                      sellerApprovalsCount={sellerApprovalsCount}
                    />
                  ))}
                </ul>
              </div>

              {/* Collapsible groups */}
              {filteredNavGroups.map((group) => {
                const canCollapseGroup = group.items.length > 1;
                const groupCollapsed = !isCollapsedView && isGroupCollapsed(group.title);

                return (
                  <div key={group.title} className="pt-1">
                    {/* Group header */}
                    {!isCollapsedView ? (
                      canCollapseGroup ? (
                        <button
                          type="button"
                          aria-expanded={!groupCollapsed}
                          onClick={() => toggleGroup(group.title)}
                          className="flex w-full items-center justify-between px-3 py-0.5 text-xs font-semibold text-muted-foreground/80 hover:text-foreground"
                        >
                          <span>{group.title}</span>
                          <span className="flex items-center gap-1">
                            {groupCollapsed && (
                              <span className="text-xs">({group.items.length})</span>
                            )}
                            <ChevronIcon expanded={!groupCollapsed} />
                          </span>
                        </button>
                      ) : (
                        <div className="px-3 py-0.5 text-xs font-semibold text-muted-foreground/80">
                          {group.title}
                        </div>
                      )
                    ) : null}

                    {/* Group items */}
                    {(!canCollapseGroup || !groupCollapsed) && (
                      <ul className="mt-0.5 space-y-0.5 px-1.5" role="list">
                        {group.items.map((item) => (
                          <React.Fragment key={item.href}>
                            {/* Sub-group separator */}
                            {item.separator && !isCollapsedView && (
                              <li className="px-2.5 pb-0.5 pt-2" aria-hidden="true">
                                <span className="text-xs font-semibold text-muted-foreground/55">
                                  {item.separator}
                                </span>
                              </li>
                            )}
                            <NavItemLink
                              item={item}
                              currentPath={currentPath}
                              collapsed={isCollapsedView}
                              productsIssuesCount={productsIssuesCount}
                              overdueInvoicesCount={overdueInvoicesCount}
                              sellerApprovalsCount={sellerApprovalsCount}
                            />
                          </React.Fragment>
                        ))}
                      </ul>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Bottom items — settings */}
            {filteredBottomItems.length > 0 && (
              <div className="mt-2 border-t border-border/50 pt-2">
                <ul className="space-y-0.5 px-1.5" role="list">
                  {filteredBottomItems.map((item) => (
                    <NavItemLink
                      key={item.href}
                      item={item}
                      currentPath={currentPath}
                      collapsed={isCollapsedView}
                      productsIssuesCount={productsIssuesCount}
                      overdueInvoicesCount={overdueInvoicesCount}
                      sellerApprovalsCount={sellerApprovalsCount}
                    />
                  ))}
                </ul>
              </div>
            )}
          </nav>
        </ScrollArea>

        {/* User menu */}
        <div className="border-t border-border p-1.5">
          <UserDropdown variant="sidebar" collapsed={isCollapsedView} />
        </div>
      </div>
    </aside>
  );
}

function HoverExpandableSidebar(props: Omit<SidebarChromeProps, 'railMode'>) {
  const desktopNav = useDesktopSidebarHover();
  const [isHoverExpanded, setIsHoverExpanded] = React.useState(false);

  const handleMouseEnter = React.useCallback(() => {
    if (desktopNav) {
      desktopNav.activatePrimary();
      return;
    }

    setIsHoverExpanded(true);
  }, [desktopNav]);

  const handleMouseLeave = React.useCallback(() => {
    if (desktopNav) {
      return;
    }

    setIsHoverExpanded(false);
  }, [desktopNav]);

  return (
    <SidebarChrome
      {...props}
      railMode={
        desktopNav
          ? {
              expanded: desktopNav.primaryExpanded,
            }
          : {
              expanded: isHoverExpanded,
              onMouseEnter: handleMouseEnter,
              onMouseLeave: handleMouseLeave,
            }
      }
    />
  );
}

/**
 * Sidebar navigation component with Russian labels.
 *
 * Features:
 * - Semantic nav element for accessibility
 * - Icon-only buttons have aria-label (ui-skills requirement)
 * - Collapsible groups with persistent state
 * - Sub-group separators within Склад
 * - Industrial/utilitarian aesthetic with clean borders
 *
 * @example
 * <Sidebar collapsed={isMobile} onCollapsedChange={setCollapsed} />
 */
export function Sidebar({
  collapsed = false,
  hoverExpand = false,
  onCollapsedChange,
  className,
}: SidebarProps) {
  // Get current path for active state using React Router
  const location = useLocation();
  const currentPath = location.pathname;

  // Get user for admin-only nav items
  const { user, status } = useAuth({ skipInitialCheck: true });
  const isAdmin = user?.role === 'admin';
  const { isSellerWorkspaceUser, profile } = useSellerWorkspaceAccess();
  const { selectedLocation } = useSellerWorkspaceLocation({
    accountId: user?.id ?? profile?.user_id ?? user?.username,
    locations: profile?.locations ?? [],
  });
  const showSellerBalances = Boolean(
    profile?.locations.some((location) => location.can_view_balance),
  );
  const showSellerProduction = canUseSellerProductionLocation(selectedLocation);
  const { canUse: canUsePayroll } = useProductionPayrollAccess();
  const { canView: canViewCameras } = useCameraAccess();

  // Fetch products issues summary for badge
  const enableGlobalBadges = status === 'authenticated' && !isSellerWorkspaceUser;

  const { data: issuesSummary } = useIssuesSummary(undefined, { enabled: enableGlobalBadges });
  const productsIssuesCount =
    issuesSummary?.products_with_issues ?? issuesSummary?.total_with_issues ?? 0;
  const { counts: invoiceCounts } = useInvoicesCounts({ enabled: enableGlobalBadges });
  const overdueInvoicesCount = invoiceCounts?.overdue ?? 0;
  const sellerApprovals = useSellerApprovalCounts(undefined, status === 'authenticated');
  const sellerApprovalsCount =
    isAdmin && sellerApprovals.data ? sellerApprovalTotal(sellerApprovals.data) : 0;

  // Collapsible group state
  const { isGroupCollapsed, toggleGroup } = useSidebarGroupState(currentPath);

  // Filter nav items based on user role
  const filteredTopLevelItems = React.useMemo(
    () => (isSellerWorkspaceUser ? [] : topLevelItems.filter((item) => !item.adminOnly || isAdmin)),
    [isAdmin, isSellerWorkspaceUser],
  );

  const filteredBottomItems = React.useMemo(
    () => (isSellerWorkspaceUser ? [] : bottomItems.filter((item) => !item.adminOnly || isAdmin)),
    [isAdmin, isSellerWorkspaceUser],
  );

  const filteredNavGroups = React.useMemo(() => {
    if (isSellerWorkspaceUser) {
      const sellerGroups = sellerWorkspacePrimaryGroups.map((group) => ({
        ...group,
        items: group.items.filter((item) => {
          if (item.href === '/stock/seller-workspace/receiving')
            return Boolean(profile?.can_use_supplier_receiving);
          if (item.href === SELLER_BALANCES_NAV_HREF) return showSellerBalances;
          if (item.href === SELLER_PRODUCTION_NAV_HREF) return showSellerProduction;
          // MD-0130: only where the store keeps expiry dates.
          if (item.href === SELLER_EXPIRY_NAV_HREF)
            return Boolean(selectedLocation?.expiry_enabled);
          // MD-0132: only where the store counts (the pilot is Wilanów).
          if (item.href === SELLER_COUNTS_NAV_HREF) return Boolean(selectedLocation?.count_enabled);
          return true;
        }),
      }));
      // A technician with only a camera grant sees no empty seller groups.
      const cameraOnly = canViewCameras && !hasSellerWork(profile);
      return [
        ...(cameraOnly ? [] : sellerGroups),
        ...(canUsePayroll ? [productionPayrollNavGroup] : []),
        ...(canViewCameras ? [storeCamerasNavGroup] : []),
      ];
    }
    return navGroups
      .map((group) => ({
        ...group,
        items: group.items.filter((item) => !item.adminOnly || isAdmin),
      }))
      .filter((group) => group.items.length > 0);
  }, [
    canUsePayroll,
    canViewCameras,
    profile,
    isAdmin,
    isSellerWorkspaceUser,
    showSellerBalances,
    showSellerProduction,
    selectedLocation?.expiry_enabled,
    selectedLocation?.count_enabled,
  ]);

  const isHoverRailMode = hoverExpand && collapsed;
  const chromeProps: Omit<SidebarChromeProps, 'railMode'> = {
    collapsed,
    className,
    currentPath,
    filteredBottomItems,
    filteredNavGroups,
    filteredTopLevelItems,
    overdueInvoicesCount,
    sellerApprovalsCount,
    onCollapsedChange,
    productsIssuesCount,
    isGroupCollapsed,
    toggleGroup,
  };

  if (isHoverRailMode) {
    return <HoverExpandableSidebar {...chromeProps} />;
  }

  return <SidebarChrome {...chromeProps} />;
}

// ============================================================================
// NavItemLink - extracted for reuse between top-level and group items
// ============================================================================

interface NavItemLinkProps {
  item: NavItem;
  currentPath: string;
  collapsed: boolean;
  productsIssuesCount: number;
  overdueInvoicesCount: number;
  sellerApprovalsCount: number;
}

interface NavItemBadge {
  count: number;
  tone: 'warning' | 'danger';
  srLabel: string;
  testIdBase: string;
}

const MAX_NAV_BADGE_COUNT = 99;

function formatNavBadgeCount(count: number): string {
  return count > MAX_NAV_BADGE_COUNT ? `${MAX_NAV_BADGE_COUNT}+` : String(count);
}

function getNavItemBadge(params: {
  href: string;
  productsIssuesCount: number;
  overdueInvoicesCount: number;
  sellerApprovalsCount: number;
}): NavItemBadge | null {
  const { href, productsIssuesCount, overdueInvoicesCount, sellerApprovalsCount } = params;

  if (href === '/stock/seller-workspace' && sellerApprovalsCount > 0) {
    return {
      count: sellerApprovalsCount,
      tone: 'warning',
      srLabel: `Ждут апрува: ${sellerApprovalsCount}`,
      testIdBase: 'sidebar-alert-seller-approvals',
    };
  }

  if (href === '/stock/products' && productsIssuesCount > 0) {
    return {
      count: productsIssuesCount,
      tone: 'warning',
      srLabel: `Товары с проблемами: ${productsIssuesCount}`,
      testIdBase: 'sidebar-alert-stock-products',
    };
  }

  if (href === '/invoices' && overdueInvoicesCount > 0) {
    return {
      count: overdueInvoicesCount,
      tone: 'danger',
      srLabel: `Просроченные фактуры: ${overdueInvoicesCount}`,
      testIdBase: 'sidebar-alert-invoices',
    };
  }

  return null;
}

function NavItemLink({
  item,
  currentPath,
  collapsed,
  productsIssuesCount,
  overdueInvoicesCount,
  sellerApprovalsCount,
}: NavItemLinkProps) {
  const fullPath = `/webadmin/app${item.href}`;
  const isActive = isNavItemActive(currentPath, item);
  const badge = getNavItemBadge({
    href: item.href,
    productsIssuesCount,
    overdueInvoicesCount,
    sellerApprovalsCount,
  });
  const collapsedAriaLabel = collapsed
    ? badge
      ? `${item.label}. ${badge.srLabel}`
      : item.label
    : undefined;

  return (
    <li>
      <Link
        to={fullPath}
        className={cn(
          // Base styles
          'group relative flex items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-sm font-medium leading-5 tracking-[0.01em]',
          // Focus ring
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
          // State styles
          isActive
            ? 'bg-[#101a2f] text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]'
            : 'text-muted-foreground hover:bg-muted/70 hover:text-foreground',
          item.href === '/stock/seller-workspace' &&
            badge &&
            'bg-warning-surface text-warning ring-1 ring-inset ring-warning-border hover:bg-warning-surface hover:text-warning',
          // Collapsed state
          collapsed && 'justify-center px-1.5',
        )}
        aria-current={isActive ? 'page' : undefined}
        aria-label={collapsedAriaLabel}
      >
        <span className="flex-shrink-0 text-current [&_svg]:size-[18px]" aria-hidden="true">
          {item.icon}
        </span>
        {collapsed ? (
          badge ? (
            <span
              data-testid={`${badge.testIdBase}-dot`}
              className={cn(
                'pointer-events-none absolute right-1.5 top-1.5 size-2 rounded-full ring-[1.5px] ring-background',
                badge.tone === 'danger'
                  ? 'bg-destructive'
                  : 'bg-warning',
              )}
              aria-hidden="true"
            />
          ) : null
        ) : (
          <>
            <span className="truncate pt-px">{item.label}</span>
            {badge && (
              <span
                data-testid={`${badge.testIdBase}-count`}
                aria-label={badge.srLabel}
                className={cn(
                  'ml-auto flex-shrink-0',
                  'inline-flex items-center justify-center',
                  'h-[18px] min-w-[18px] rounded-full px-1',
                  'text-xs font-semibold leading-none',
                  // Soft pastel style per design rules
                  badge.tone === 'danger'
                    ? 'bg-destructive/10 text-foreground'
                    : 'bg-warning-surface text-foreground',
                )}
              >
                {formatNavBadgeCount(badge.count)}
              </span>
            )}
          </>
        )}
        {!collapsed && badge ? <span className="sr-only">{badge.srLabel}</span> : null}
      </Link>
    </li>
  );
}

// ============================================================================
// Icons - Simple, functional SVG icons
// ============================================================================

function ChevronIcon({ expanded }: { expanded: boolean }) {
  return (
    <svg
      className={cn('size-3 transition-transform', expanded && 'rotate-90')}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
    </svg>
  );
}

function MenuToggleIcon({ collapsed }: { collapsed: boolean }) {
  return (
    <svg className="size-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      {collapsed ? (
        // Expand icon (chevron right)
        <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
      ) : (
        // Collapse icon (chevron left)
        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
      )}
    </svg>
  );
}

export { navItems, navGroups };
